/**
 * How a URL names an organization.
 *
 * Plain functions, no React. The rule that `/suppliers` inside NaveedOrg is
 * `/org/naveedorg/suppliers` is the sort of thing that gets applied in dozens
 * of places and has to mean the same thing in all of them, so it is written
 * once, here, where it can be read and tested without rendering anything.
 *
 * orgNavigation.jsx binds these to react-router; AppRoutes.jsx declares the
 * matching route. Nothing else should be constructing these strings by hand.
 */

/**
 * Where one organization's screens live.
 *
 * The single definition of the prefix. Almost everything derives its URLs from
 * the base in context instead; this is for the places that cannot — the route
 * table, and the switcher, which needs the base of an organization it is not
 * currently in.
 */
export const orgBasePath = (slug) => `/org/${encodeURIComponent(slug)}`;

/**
 * Resolves one destination against an organization base.
 *
 * Only ABSOLUTE, in-app paths are rewritten:
 *
 *  - a RELATIVE path is already resolved against the current route by the
 *    router, and prefixing it would resolve it twice;
 *  - an EXTERNAL url is not ours to touch;
 *  - anything already under `/org/` has been prefixed once, which makes this
 *    idempotent and means a caller who passes a full path by hand — the
 *    organization switcher, say — is not punished for it.
 *
 * `/organization` is deliberately NOT caught by that last rule: the guard
 * tests `/org/`, with the slash, so the organization-profile screen keeps
 * working.
 *
 * @param {string} base '' outside an organization, '/org/<handle>' inside one
 * @param {string|{pathname?: string}} to
 */
export function orgPath(base, to) {
  if (!base || to == null) return to;

  if (typeof to === 'object') {
    // A location descriptor: { pathname, search, hash }.
    return to.pathname ? { ...to, pathname: orgPath(base, to.pathname) } : to;
  }

  if (typeof to !== 'string' || !to.startsWith('/')) return to;
  if (to === base || to.startsWith('/org/')) return to;

  // The organization's own root: '/' must give '/org/acme', not '/org/acme/'.
  return to === '/' ? base : `${base}${to}`;
}
